
import { defineComponent } from "vue";
import {
  getSamplingPointById,
  getSamplingPointLabelling,
  postSamplingPointLabelling,
  putSamplingPoint,
} from "@/service/samplingpoint.service";
import {
  getDetailStation,
  getStations,
  updateStationConfigurationIssue,
} from "@/service/station.service";
import { getTmoa } from "@/service/tmoa.service";
import { SamplingPoint, SamplingPointLabelling } from "@/types/samplingpoint.type";
import { Station } from "@/types/station.type";
import { Tmoa } from "@/types/tmoa.type";
import { Filter } from "@/types/filter.type";
import { ElForm, ElMessage } from "element-plus";
import { MassiveCapture } from "@/types/massiveCapture.type";
import { CameraConfiguration } from "@/types/cameraconfiguration.type";
import {
  getCamerasConfigurationByStationId,
  updateCameraModelStation,
} from "@/service/cameraConfiguration.service";
import { RootState } from "@/types/state.type";
import { useStore } from "vuex";
import WaterTypeList from "@/components/waterType/WaterTypeList.component.vue";
import { WaterType } from "@/types/waterType.type";
import { getWaterTypes } from "@/service/waterType.service";
import TmoaInfoDialog from "@/components/samplingPoints/TmoaInfoDialog.component.vue";
import { TmoaLocal } from "@/components/samplingPoints/SamplingPointForm.component.vue";
import { sortByType } from "@/utils/tmoa.util";
import {
  getSamplingPointsImageStatus,
  putSamplingPointsImageStatus,
} from "@/service/samplingPointImageStatus.service";
import { SamplingPointImageStatus } from "@/types/issueMonitoring.type";

export default defineComponent({
  components: {
    WaterTypeList,
    TmoaInfoDialog,
  },
  data() {
    return {
      samplingPoint: undefined as SamplingPoint | undefined,
      station: undefined as Station | undefined,
      stationCameras: undefined as Station | undefined,
      path1: "" as string,
      types: [
        { value: "tmoa", label: "Tipología de agua" },
        { value: "timetable", label: "Horario" },
      ],
      tmoasOptions: [] as Array<{ label: string; value: number }>,
      tmoaActiveName: "" as string,
      samplingPointForm: {} as SamplingPoint,
      samplingPointLabel: {} as SamplingPointLabelling,
      showForm: "samplingPoint" as "station" | "samplingPoint",
      camerasConfiguration: [] as Array<CameraConfiguration>,
      store: useStore<RootState>(),
      waterType: [] as WaterType[],
      options_1: [
        {
          value: "valido",
          label: "Válido",
          children: [
            {
              value: "determinado",
              label: "determinado",
            },
            {
              value: "indeterminado",
              label: "indeterminado ",
            },
          ],
        },
        { value: "invalido", label: "Inválido" },
        { value: "anomalia", label: "Anomalía" },
      ],
      isTmoaDialogVisible: false,
      tmoas: [] as TmoaLocal[],
      colorOptions: [
        // Colores por defecto
        "#67B7DC",
        "#6794DC",
        "#6771DC",
        "#8067DC",
        "#A367DC",
        "#C767DC",
        "#DC67CE",
        "#D667DC",
        "#E167DC",
        "#F167DC",

        // Amarillos
        "#F2E5A0",
        "#E8D98B",
        "#D9C87A",

        // Verdes
        "#A6B88F",
        "#8FA67A",
        "#768F63",
        "#6B7F58",

        // Marrones
        "#C2A27A",
        "#A88963",
        "#8B6F4E",

        // Azules
        "#A7B6C9",
        "#8FA1B5",
        "#768A9A",

        // Turquesa
        "#88A8A0",
        "#72948C",

        // Grises
        "#C7C7C2",
        "#B3B3AE",
        "#9A9A95",

        // Ocres
        "#B8A97A",
        "#9E8E6A",

        // Anomalía
        "#F37878",
      ],
      imageStatus: [] as SamplingPointImageStatus[],
    };
  },
  computed: {
    id(): string {
      return (this.$route.query.samplingPoint || "") as string;
    },
    canEdit(): boolean {
      const userId = this.store.state.auth.user?.id;
      const responsibleList = this.samplingPoint?.responsible || [];
      return userId !== undefined && responsibleList.some((r) => r.id === userId);
    },
  },
  mounted() {
    this.loadData();
  },

  methods: {
    async loadTmoas() {
      this.tmoas = await getTmoa();

      this.tmoas.forEach((tmoa) => {
        if (tmoa.file && !tmoa.fileName) {
          tmoa.fileName = this.getFilename(tmoa.file) || "";
        }
      });
      let colorIndex = 0;

      this.tmoas.forEach((tmoa) => {
        const sp = tmoa.samplingPoint as unknown as SamplingPoint;
        if (sp.id === this.samplingPoint?.id) {
          if (!tmoa.color) {
            tmoa.color = this.colorOptions[colorIndex];
            colorIndex++;
          }
        }
      });

      this.tmoas = sortByType(this.tmoas);
    },
    async loadData() {
      try {
        const idNum = Number(this.id);
        if (isNaN(idNum)) return;

        // obtener samplingpoint
        const [samplingPoint, samplingPointLabel] = await Promise.all([
          getSamplingPointById(idNum),
          getSamplingPointLabelling(idNum),
        ]);
        this.samplingPoint = samplingPoint;
        this.samplingPointLabel = samplingPointLabel || ({} as SamplingPointLabelling);

        if (!this.samplingPoint) return;
        this.samplingPointForm = this.samplingPoint;
        this.station = this.samplingPoint.station as Station;

        const filterStatus: Filter = {
          fields: {
            "samplingpointimagestatus.samplingPointId": {
              operator: "equal",
              value: this.samplingPoint.id,
            },
          },
        };
        const stationFilter: Filter = {
          fields: { "station.station": { operator: "contains", value: this.station?.station } },
        };

        const [imageStatusRes, _tmoas, camerasConfig, waterTypes, stationsRes] = await Promise.all([
          getSamplingPointsImageStatus(undefined, undefined, filterStatus),
          this.loadTmoas(),
          this.station?.id
            ? getCamerasConfigurationByStationId(this.station.id)
            : Promise.resolve([]),
          getWaterTypes(this.samplingPoint.id || 0),
          getStations(1, 1, stationFilter),
        ]);

        this.imageStatus = imageStatusRes.data;
        this.camerasConfiguration = camerasConfig;
        this.waterType = waterTypes;

        const foundStation = stationsRes.data?.[0];
        if (!foundStation) {
          console.warn("No se encontró la estación");
          return;
        }

        this.station = foundStation;

        this.stationCameras = await getDetailStation(this.station.station || "");

        this.initializeMassiveCapture();

        if (this.station.massiveCapture?.active && !this.station.massiveCapture.path) {
          this.updatedPathPart1();
        }

        if (this.station.massiveCapture?.type === "tmoa") {
          await this.changeOptions();
        }
      } catch (e) {
        console.error("Error en loadData: ", e);
      }
    },

    initializeMassiveCapture() {
      if (
        this.station &&
        (this.station.massiveCapture === null || this.station.massiveCapture === undefined)
      ) {
        this.station.massiveCapture = {
          active: false,
          fromDateTime: undefined,
          toDateTime: undefined,
          path: undefined,
          type: "timetable",
          typeTMOA: [],
          tmoaActive: undefined,
        } as unknown as MassiveCapture;
      }

      if (this.station) {
        this.station.threshold = this.station.threshold ?? 0;
        this.station.night = this.station.night ?? 0;
        this.station.wake_time = this.station.wake_time ?? undefined;
        this.station.sleep_time = this.station.sleep_time ?? undefined;
        this.station.position = this.station.position ?? undefined;
      }
    },

    async getInfoSamplingPoint() {
      if (this.station?.massiveCapture?.type === "tmoa") {
        await this.changeOptions();
      }
    },

    async changeOptions() {
      if (
        this.station?.massiveCapture?.type === "tmoa" &&
        this.tmoasOptions.length === 0 &&
        this.samplingPoint?.id
      ) {
        const filter: Filter = {
          fields: {
            "tmoa.samplingPointId": {
              operator: "equal",
              value: this.samplingPoint.id,
            },
          },
        };

        const tmoas = await getTmoa(filter);
        this.tmoasOptions = (tmoas || []).map((_: Tmoa) => ({
          value: _.value as number,
          label: _.name ?? `${_.value}`,
        }));

        const tmoaActiveValue = this.station?.massiveCapture?.tmoaActive;
        if (tmoaActiveValue) {
          this.tmoaActiveName = tmoas.find((_) => _.value === tmoaActiveValue)?.name ?? "";
        }

        if (this.station?.massiveCapture?.typeTMOA) {
          this.station.massiveCapture.typeTMOA = this.station.massiveCapture.typeTMOA.map(Number);
        }
      }
    },

    updatedPathPart1() {
      if (
        this.station?.massiveCapture &&
        this.station.station &&
        this.station.massiveCapture.active &&
        !this.station.massiveCapture.path
      ) {
        this.path1 = `${this.samplingPoint?.code ?? this.station.station}`;
        this.station.massiveCapture.path = `/${this.path1}/`;
      }
    },

    updatedPath() {
      if (
        this.station?.massiveCapture &&
        this.station.station &&
        this.station.massiveCapture.active
      ) {
        this.station.massiveCapture.path = `/${this.path1}/${
          this.station.massiveCapture.camera ?? ""
        }/`;
      }
    },

    ckeckCamera(camera: CameraConfiguration) {
      let processTime = [] as string[];
      if (camera?.imageProcessingTime) {
        this.camerasConfiguration.forEach((_) => {
          if (_?.imageProcessingTime) {
            if (_?.imageProcessingTime && !processTime.includes(_?.imageProcessingTime)) {
              if (
                (_?.imageProcessingTime === "ALL_DAY" &&
                  (processTime.includes("DAY") || processTime.includes("NIGHT"))) ||
                ((_?.imageProcessingTime === "DAY" || _?.imageProcessingTime === "NIGHT") &&
                  processTime.includes("ALL_DAY"))
              ) {
                ElMessage.warning(this.$t("warning.confNotAllowed"));
                camera.imageProcessingTime = undefined;
              } else {
                processTime.push(_?.imageProcessingTime);
              }
            } else {
              ElMessage.warning(this.$t("warning.confProcessingTime"));
              camera.imageProcessingTime = undefined;
            }
          }
        });
      }
    },

    getWaterType(valor: WaterType) {
      this.waterType.push(valor);
      (this.options_1[0].children![0] as any).children = this.waterType.map((_) => ({
        label: _.name,
        value: _.name,
      }));
    },

    async updateCamerasModelStation(cameras: CameraConfiguration[]) {
      let canConfiguration = true;
      cameras.forEach(async (camera) => {
        if (camera.modele && !camera.imageProcessingTime && camera.camera?.name) {
          ElMessage.warning(this.$t("warning.confNotCorrectTime", { code: camera.camera.name }));
          canConfiguration = false;
        }
      });
      if (canConfiguration) {
        cameras.forEach(async (camera) => {
          await updateCameraModelStation(
            camera.id as number,
            camera.modele as boolean,
            camera.station?.id as number,
            camera.imageProcessingTime,
            camera.dirtThreshold
          );
        });
      }
    },

    async doSaveStation() {
      if (!this.station) return;
      try {
        await updateStationConfigurationIssue(this.station);

        await this.loadData();

        ElMessage({
          message: "Configuración guardada",
          type: "success",
        });
        // this.$router.back();
      } catch (e) {
        ElMessage({
          message: "Error al guardar",
          type: "error",
        });
      }
    },

    async doSaveSamplingPoint() {
      try {
        const formRef = this.$refs["samplingPointFormRef"] as
          | InstanceType<typeof ElForm>
          | undefined;

        if (formRef) {
          const valid = await formRef.validate();
          if (!valid) {
            ElMessage.warning("Revisa los campos del formulario");
            return;
          }
        }

        this.samplingPointForm.yBright = this.samplingPointForm.yBright || undefined;
        this.samplingPointForm.xBright = this.samplingPointForm.xBright || undefined;
        this.samplingPointForm.dyBright = this.samplingPointForm.dyBright || undefined;
        this.samplingPointForm.dxBright = this.samplingPointForm.dxBright || undefined;

        // Guardar etiquetado
        this.samplingPointLabel.samplingPointId = await getSamplingPointById(
          this.samplingPointForm.id || 0
        );

        await Promise.all([
          postSamplingPointLabelling(this.samplingPointLabel),
          putSamplingPoint(this.samplingPointForm),
          this.updateCamerasModelStation(this.camerasConfiguration),
          this.saveImageStatuses(),
        ]);

        await this.loadData();

        ElMessage.success("Configuración guardada");
      } catch (e) {
        console.error("Error al guardar SamplingPoint:", e);
        ElMessage.error("Error al guardar");
      }
    },

    async saveImageStatuses() {
      try {
        const promises = this.imageStatus.map((status) => {
          return putSamplingPointsImageStatus(status);
        });

        await Promise.all(promises);
      } catch (error) {
        console.error("Error al guardar:", error);
      }
    },

    onCancel() {
      this.$router.back();
    },

    openTmoaDialog() {
      this.isTmoaDialogVisible = true;
    },
    async handleSaveTmoas(updatedTmoas: any) {
      this.isTmoaDialogVisible = false;
    },
    getFilename(path: string): string | null {
      const parts = path.split("/");
      if (parts.length <= 3) return null;
      return parts.slice(3).join("/");
    },
  },
});
